// src/pages/group/entry/Group_update.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  CircularProgress,
  Stack,
  Paper,
  Chip,
  Autocomplete,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ENDPOINT, API_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Group_update = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingPermissions, setFetchingPermissions] = useState(false);
  const [fetchingGroup, setFetchingGroup] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // ---------------- Fetch group detail ----------------
  const fetchGroupDetail = async () => {
    setFetchingGroup(true);
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(ENDPOINT.GROUP_DETAIL(id), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const group = response.data.group;
        setName(group.name);
        setPermissions(group.permissions);
      } else {
        toast.error("Failed to fetch group details");
      }
    } catch (error) {
      console.error("Error fetching group detail:", error);
      toast.error(
        error.response?.data?.message ||
          "Something went wrong while fetching group detail"
      );
    } finally {
      setFetchingGroup(false);
    }
  };

  // ---------------- Fetch all permissions ----------------
  const fetchPermissions = async (search = "") => {
    setFetchingPermissions(true);
    try {
      const token = localStorage.getItem("accessToken");
      const url = search
        ? `${API_BASE_URL}/permissions/?search=${encodeURIComponent(search)}`
        : `${API_BASE_URL}/permissions/`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const perms = response.data.results?.map((p) => p.codename) || [];
      setAllPermissions(perms);
    } catch (err) {
      console.error("Error fetching permissions:", err);
      toast.error("Failed to fetch permissions");
    } finally {
      setFetchingPermissions(false);
    }
  };

  useEffect(() => {
    fetchGroupDetail();
    fetchPermissions();
  }, [id]);

  // ---------------- Handle search input ----------------
  const handleSearchInputChange = (event, value) => {
    setSearchTerm(value);
    fetchPermissions(value);
  };

  // ---------------- Handle submit ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const body = { name, permissions };

      const response = await axios.put(ENDPOINT.GROUP_UPDATE(id), body, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        // ✅ navigate with state to show toast in list page
        navigate("/groups/list", { state: { toastMessage: response.data.message } });
      } else {
        toast.error("Failed to update group");
      }
    } catch (error) {
      console.error("Error updating group:", error);
      toast.error(
        error.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------------- UI ----------------
  if (fetchingGroup) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 5 }}>
      {/* ToastContainer */}
      <ToastContainer position="top-right" autoClose={3000} />

      <Paper sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" mb={3}>
          ✏️ Update Group
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Group Name */}
            <TextField
              label="Group Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />

            {/* Permissions Searchable Autocomplete */}
            <Autocomplete
              multiple
              options={allPermissions}
              value={permissions}
              onChange={(event, newValue) => setPermissions(newValue)}
              onInputChange={handleSearchInputChange}
              filterSelectedOptions
              getOptionLabel={(option) => option}
              renderTags={(selected, getTagProps) =>
                selected.map((value, index) => (
                  <Chip key={value} label={value} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Permissions"
                  placeholder="Search permissions..."
                  disabled={fetchingPermissions}
                />
              )}
            />

            {/* Buttons */}
            <Stack direction="row" justifyContent="space-between" mt={2}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate("/groups/list")}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading || fetchingPermissions}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Update Group"
                )}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default Group_update;